import { Modal, ModalHeader, ModalBody, ModalFooter } from 'components/ui/Modal'
import { useTranslation } from 'react-i18next'
import { OdinInput } from '@/components/ui/odin/form/odin-input'
import { useForm, Controller, Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { optional, required, requiredEmail } from 'utils/validations'
import { OdinButton } from '@/components/ui/odin/odin-button'
import { OdinTextarea } from '@/components/ui/odin/form/odin-textarea'
import { openTicket, useGetTicketAdditionalFields } from 'api/tickets'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { settingsSelectors } from 'store/settings'
import { showSuccessToast } from 'utils/toast'
import { AdditionalFieldTypeEnum, TicketAdditionalField } from 'types/ticket-additional-field'
import { OdinRadioGroup, OdinRadio } from '@/components/ui/odin/form/odin-radio'
import { clsx } from 'clsx'
import { useGetBotMode } from 'hooks/useGetBotMode'
import { useModal } from 'providers/ModalProvider'
import { analytics, ANALYTICS_EVENTS } from 'services/analytics'

type TicketFormValues = Record<string, string>

export const OpenTicketModal = () => {
  const { t } = useTranslation()
  const [isLogged] = useState(useSelector(settingsSelectors.isLogged)) // freeze isLogged value to prevent ui change when submitting
  const { data: ticketAdditionalFields } = useGetTicketAdditionalFields()
  const [loading, setLoading] = useState(false)
  const { isAppMode } = useGetBotMode()
  const { closeModal } = useModal()

  const defaultValues = useMemo(() => {
    const base: TicketFormValues = { email: '', title: '', description: '' }
    ticketAdditionalFields?.forEach((field: TicketAdditionalField) => {
      base[field.name] = ''
    })
    return base
  }, [ticketAdditionalFields])

  const schema = useMemo(() => {
    const shape: Record<string, z.ZodTypeAny> = {
      email: isLogged ? optional : requiredEmail,
      title: required,
      description: required,
    }
    ticketAdditionalFields?.forEach((field: TicketAdditionalField) => {
      shape[field.name] = field.isRequired ? required : optional
    })
    return z.object(shape)
  }, [ticketAdditionalFields, isLogged])

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<TicketFormValues>({
    resetOptions: { keepDirtyValues: true },
    resolver: zodResolver(schema) as Resolver<TicketFormValues>,
    mode: 'onSubmit',
    // Dynamic fields arrive async; `values` keeps the form keys in sync once loaded.
    values: defaultValues,
  })

  const onSubmit = async (values: TicketFormValues) => {
    try {
      setLoading(true)
      await openTicket({
        email: values.email,
        title: values.title,
        description: values.description,
        additionalTicketInfo: ticketAdditionalFields?.reduce((acc: TicketFormValues, field: TicketAdditionalField) => {
          acc[field.name] = values[field.name]
          return acc
        }, {}),
      })
      setLoading(false)
      analytics.track(ANALYTICS_EVENTS.TICKET_CREATED)
      showSuccessToast(t('ticketOpenedSuccess'))
      closeModal()
    } catch (e) {
      console.error({ e })
      setLoading(false)
    }
  }

  return (
    <Modal onClose={closeModal}>
      <ModalHeader title={t('needToOpenTicket')} icon="headset" />
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody className={clsx(isAppMode ? 'modal-container-app' : `modal-container`)}>
          <div className={'scrollbar-hidden flex flex-col gap-y-2 overflow-y-auto px-2 py-1'}>
            {!isLogged && <OdinInput label={t('email')} error={errors.email?.message} {...register('email')} />}
            <OdinInput label={t('title')} error={errors.title?.message} {...register('title')} />
            <OdinTextarea label={t('description')} error={errors.description?.message} {...register('description')} />
            {ticketAdditionalFields?.map((field: TicketAdditionalField) => {
              if (field.additionalFieldType === AdditionalFieldTypeEnum.FREE) {
                return (
                  <OdinInput
                    type={'text'}
                    key={field.name}
                    label={field.name}
                    error={errors[field.name]?.message}
                    {...register(field.name)}
                  />
                )
              }
              if (field.additionalFieldType === AdditionalFieldTypeEnum.SELECT) {
                return (
                  <Controller
                    key={field.name}
                    name={field.name}
                    control={control}
                    render={({ field: f }) => (
                      <TicketAdditionalFieldSelect
                        field={field}
                        value={f.value}
                        error={errors[field.name]?.message}
                        onChange={f.onChange}
                      />
                    )}
                  />
                )
              }
              return null
            })}
          </div>
        </ModalBody>
        <ModalFooter>
          <OdinButton type={'submit'} className={'w-full'} loading={loading}>
            <span className={'text-sm'}>{t('confirm')}</span>
          </OdinButton>
        </ModalFooter>
      </form>
    </Modal>
  )
}

type TicketAdditionalFieldSelectProps = {
  field: TicketAdditionalField
  value?: string
  error?: string
  onChange: (value: string) => void
}
export const TicketAdditionalFieldSelect = ({ field, value, error, onChange }: TicketAdditionalFieldSelectProps) => {
  const { t } = useTranslation()
  return (
    <div key={field.name}>
      <div className={'mb-1 text-sm'}>{field.name}</div>
      <div
        className={clsx('flex flex-col gap-y-2 rounded-md border bg-muted p-2', {
          'border-red-700 text-red-700': !!error,
          'border-input': !error,
        })}
      >
        <OdinRadioGroup value={value} onChange={onChange}>
          {field.options?.map((option: string, index) => (
            <OdinRadio key={index} value={option} label={option} />
          ))}
        </OdinRadioGroup>
      </div>
      {error && <p className={'animate-in text-xs text-red-700 fade-in'}>{t(error)}</p>}
    </div>
  )
}
